import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TranslateService } from '@ngx-translate/core';
import { InstallationFeaturesStore } from '@store';
import { Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
  DhwHygieneCommands,
  DhwHygieneProperties,
  DhwHygieneTriggerCommands,
  DhwHygieneTriggerProperties,
  Feature,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
} from '../../../../../../projects/client';
import { ViConfirmDialogService } from '../../../../../../projects/ui';
import { HygieneActivationSchedule } from './hygiene';
@Component({
  selector: 'app-hygiene',
  templateUrl: './hygiene.component.html',
  styleUrls: ['./hygiene.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HygieneComponent implements OnInit, OnDestroy {
  hygieneRepeatabilityValue: { value: HygieneActivationSchedule; viewValue: string };
  hygieneActivationScheduleOptions: { value: HygieneActivationSchedule; viewValue: string }[] = [
    {
      value: HygieneActivationSchedule.Everyday,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_EVERYDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Mon,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_MONDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Tue,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_TUESDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Wed,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_WEDNESDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Thu,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_THURSDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Fri,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_FRIDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Sat,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_SATURDAY'
      ),
    },
    {
      value: HygieneActivationSchedule.Sun,
      viewValue: this.translateService.instant(
        '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_PROGRAM_REPEATABILITY_SUNDAY'
      ),
    },
  ];
  hygieneActivationTimeValue: string;
  hygieneActivationTimeOptions: string[];

  dhwHygiene$: Observable<Feature<DhwHygieneProperties, DhwHygieneCommands>>;
  dhwHygieneTrigger$: Observable<Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>>;

  readonly featureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    private translateService: TranslateService,
    private viConfirmModalService: ViConfirmDialogService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.dhwHygiene$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwHygiene
    );
    this.dhwHygieneTrigger$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwHygieneTrigger
    );
    this.dhwHygieneTrigger$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((feature) => {
      if (feature?.isEnabled) {
        this.hygieneRepeatabilityValue = this.getHygieneRepeatabilityValue(feature);
        this.hygieneActivationTimeOptions = this.getHygieneActivationTimeOptions(feature);
        this.hygieneActivationTimeValue = this.getHygieneActivationTimeValue(feature);
      }
    });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onHygieneActivationRepeatabilityChange(
    option: { value: HygieneActivationSchedule },
    feature: Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>
  ): void {
    const hour = feature.properties.startHour.value;
    const minute = feature.properties.startMinute.value;

    if (option.value === HygieneActivationSchedule.Everyday) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwHygieneTrigger,
        [+hour, +minute],
        [this.featurePropertyKey.startHour, this.featurePropertyKey.startMinute],
        null,
        FeatureCommands.triggerDaily
      );
    } else {
      const day = option.value;
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwHygieneTrigger,
        [day, +hour, +minute],
        [
          this.featurePropertyKey.weekday,
          this.featurePropertyKey.startHour,
          this.featurePropertyKey.startMinute,
        ],
        null,
        FeatureCommands.triggerOncePerWeek
      );
    }
  }

  onHygieneActivationTimeChange(
    option: { value: string },
    feature: Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>
  ): void {
    const everydayLength = 7;
    const timeValueSplit = option.value.split(':');
    const hour = timeValueSplit[0];
    const minute = timeValueSplit[1];
    const isEveryDay = feature.properties.weekdays.value.length === everydayLength;

    if (isEveryDay) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwHygieneTrigger,
        [+hour, +minute],
        [this.featurePropertyKey.startHour, this.featurePropertyKey.startMinute],
        null,
        FeatureCommands.triggerDaily
      );
    } else {
      const day = feature.properties.weekdays.value[0];
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwHygieneTrigger,
        [day, +hour, +minute],
        [
          this.featurePropertyKey.weekday,
          this.featurePropertyKey.startHour,
          this.featurePropertyKey.startMinute,
        ],
        null,
        FeatureCommands.triggerOncePerWeek
      );
    }
  }

  onHygieneEnabledToggle(enabledToggle: MatSlideToggle, switchToEnabled: boolean): void {
    enabledToggle.toggle();

    this.viConfirmModalService
      .open({
        title: this.translateService.instant(
          switchToEnabled
            ? this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ENABLE_SWITCH_TRUE'
              )
            : this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ENABLE_SWITCH_FALSE'
              )
        ),
        content: this.translateService.instant(
          switchToEnabled
            ? this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ENABLE_DESCRIPTION_TRUE'
              )
            : this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ENABLE_DESCRIPTION_FALSE'
              )
        ),
        confirmBtn: this.translateService.instant('GENERAL.BUTTON.CONFIRM'),
        cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
      })
      .pipe(take(1))
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.dhwHygiene,
            true,
            FeaturePropertyKey.enabled,
            '',
            switchToEnabled ? FeatureCommands.enable : FeatureCommands.disable
          );
        }
      });
  }

  onHygieneActivateToggle(activationToggle: MatSlideToggle): void {
    activationToggle.toggle();

    this.viConfirmModalService
      .open({
        title: this.translateService.instant(
          this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ACTIVATE_SWITCH_TRUE'
          )
        ),
        content: this.translateService.instant(
          this.translateService.instant(
            '+INSTALLATIONS.INSTALLATION.WARM_WATER.HYGIENE_ACTIVATE_DESCRIPTION_TRUE'
          )
        ),
        confirmBtn: this.translateService.instant('GENERAL.BUTTON.CONFIRM'),
        cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
      })
      .pipe(take(1))
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.dhwHygiene,
            true,
            FeaturePropertyKey.active,
            '',
            FeatureCommands.activate
          );
        }
      });
  }
  private getHygieneRepeatabilityValue(
    feature: Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>
  ): {
    value: HygieneActivationSchedule;
    viewValue: string;
  } {
    const everydayLength = 7;
    const days = feature.properties.weekdays?.value;

    let scheduleValue: HygieneActivationSchedule;
    if (days.length === everydayLength) {
      scheduleValue = HygieneActivationSchedule.Everyday;
    } else {
      scheduleValue = HygieneActivationSchedule[days[0]];
    }

    const value = this.hygieneActivationScheduleOptions.find(
      (entry) => entry.value === scheduleValue
    );

    return value;
  }

  private getHygieneActivationTimeValue(
    feature: Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>
  ): string {
    const hour = feature.properties.startHour.value;
    const minute = feature.properties.startMinute.value;

    const hourAdjusted = hour.toString().length === 1 ? `0${hour}` : hour;
    const minuteAdjusted = minute.toString().length === 1 ? `${minute}0` : minute;

    return `${hourAdjusted}:${minuteAdjusted}`;
  }

  private getHygieneActivationTimeOptions(
    feature: Feature<DhwHygieneTriggerProperties, DhwHygieneTriggerCommands>
  ): string[] {
    const hoursConstrains = feature.commands.triggerDaily.params.startHour.constraints;
    const minutesConstrains = feature.commands.triggerDaily.params.startMinute.constraints;

    const options = [];
    for (let i = hoursConstrains.min; i <= hoursConstrains.max; i = i + hoursConstrains.stepping) {
      for (
        let j = minutesConstrains.min;
        j <= minutesConstrains.max;
        j = j + minutesConstrains.stepping
      ) {
        const hour = i.toString().length === 1 ? `0${i}` : i;
        const minute = j.toString().length === 1 ? `${j}0` : j;
        options.push(`${hour}:${minute}`);
      }
    }

    return options;
  }
}
